import { GbgrundstueckService, GbrechtService, GrundstueckDetailEntryMeta } from '../../core/gb-api';
import { Component, Directive, Input, OnDestroy, OnInit } from '@angular/core';
import { Observable, of, Subscription } from 'rxjs';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { TimepointService } from '../../core/services/timepoint.service';
import { Egrid } from '../../core/gwr-api';
import { ErweiterungConfigResponse, RubrikConfigEntry } from '../../core/base-api';
import { FormatConfigService } from '../../core/services/format-config.service';
import { AppConfigService } from '../../core/services/app-config.service';
import { filter, map, share, startWith } from 'rxjs/operators';
import { GrundstueckRubrikService } from '../grundstueck-rubriken/grundstueck-rubrik.service';
import { GrundstueckRubrikComponentVM } from '../grundstueck-rubriken/grundstueck-rubrik-component-v-m';
import { NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import ActiveErweiterungenEnum = ErweiterungConfigResponse.ActiveErweiterungenEnum;
import RubrikEnum = RubrikConfigEntry.RubrikEnum;

@Component({
  selector: 'cap-grundstueck-menu',
  templateUrl: './grundstueck-menu.component.html',
  styleUrls: ['./grundstueck-menu.component.scss'],
  imports: [RouterLinkActive, NgClass, RouterLink, TranslatePipe]
})
export class GrundstueckMenuComponent implements OnInit, OnDestroy {
  @Input()
  fixMenu: boolean;
  @Input()
  grundstueckEntry: GrundstueckDetailEntryMeta;

  public menuItems: Array<SubMenuItem> = [];
  private grundstueckId: string;
  private grundstueckRubriken$: Observable<Array<GrundstueckRubrikComponentVM>>;
  private subscription: Subscription = new Subscription();

  constructor(
    private rechtService: GbrechtService,
    private grundstueckService: GbgrundstueckService,
    private route: ActivatedRoute,
    private router: Router,
    private appConfigService: AppConfigService,
    private timepointService: TimepointService,
    private appFormatConfigService: FormatConfigService,
    private grundstueckRubrikService: GrundstueckRubrikService
  ) {
    keysToTranslate<string>([
      'grundstueck.subnav.Uebersicht',
      'grundstueck.subnav.GrundbuchVermessung',
      'grundstueck.subnav.BeschreibendeDaten',
      'grundstueck.subnav.Eigentum',
      'grundstueck.subnav.Dienstbarkeiten',
      'grundstueck.subnav.Grundlasten',
      'grundstueck.subnav.Vormerkungen',
      'grundstueck.subnav.Anmerkungen',
      'grundstueck.subnav.Grundpfandrechte',
      'grundstueck.subnav.HaengigeGeschaefte',
      'grundstueck.subnav.GwrObjekte',
      'grundstueck.subnav.neskoab',
      'grundstueck.subnav.OerebKataster',
      'grundstueck.subnav.GwrObjekte',
      'grundstueck.subnav.Rangregelungen'
    ]);
    this.route.queryParamMap.subscribe((q) => {
      const id = q.get('id');
      if (id === null || (this.grundstueckId && id === this.grundstueckId)) {
        return;
      }
      if (!this.grundstueckEntry || (this.grundstueckEntry.id && id !== this.grundstueckEntry.id.id)) {
        const z = this.timepointService.timepointCtx;
        this.grundstueckService
          .loadGrundstueckById(id, this.appConfigService.einstellungHistorischeDaten, z.timepointIso, z.timepointId)
          .subscribe((gsDetail) => {
            this.grundstueckEntry = gsDetail;
            this.init(id);
          });
      } else {
        this.init(id);
      }
    });
    this.grundstueckRubriken$ = this.grundstueckRubrikService.getRubriken(RubrikEnum.GB_DETAIL_GRUNDSTUECK).pipe(share());
  }

  get menuCSSClasses() {
    return this.fixMenu ? 'nav flex-column fixedmenu' : 'nav flex-column';
  }

  public getMenuComparator(a: SubMenuItem, b: SubMenuItem) {
    return a.menuOrder - b.menuOrder;
  }

  getQueryParams(item: SubMenuItem) {
    const params = { id: item.id, timepointId: this.timepointService.timepointCtx.timepointId };
    if (item instanceof GwrSubmenuItem) {
      const gwrItem: GwrSubmenuItem = item;
      if (gwrItem.egrid) {
        params['egrid'] = gwrItem.egrid.value;
      }
      params['bfsnummer'] = gwrItem.bfsnummer;
      params['grundstuecknummer'] = gwrItem.grundstuecknummer;
    }
    return params;
  }

  ngOnInit() {
    const idObservable: Observable<string> = of(this.grundstueckEntry.id.id);
    this.menuItems.push(
      new SubMenuItem(
        idObservable,
        of('/grundstueck/detail/uebersicht'),
        'grundstueck.subnav.Uebersicht',
        0,
        of(true),
        of(true),
        false,
        true,
        of(true),
        of(0),
        this.router
      )
    );
    this.menuItems.push(
      new SubMenuItem(
        idObservable,
        of('/grundstueck/detail/gbav'),
        'grundstueck.subnav.GrundbuchVermessung',
        0,
        of(true),
        of(true),
        false,
        true,
        of(true),
        of(1),
        this.router
      )
    );
    this.menuItems.push(
      new SubMenuItem(
        idObservable,
        of('/grundstueck/detail/beschreibung'),
        'grundstueck.subnav.BeschreibendeDaten',
        1,
        of(true),
        of(this.grundstueckEntry.permissions?.beschreibung),
        false,
        true,
        this.isRubrikConfigured('detailGrundstueckBeschreibung'),
        this.getRubrikConfiguredIndex('detailGrundstueckBeschreibung'),
        this.router
      )
    );
    this.menuItems.push(
      new SubMenuItem(
        idObservable,
        of('/grundstueck/detail/eigentum'),
        'grundstueck.subnav.Eigentum',
        1,
        of(true),
        of(this.grundstueckEntry.permissions?.eigentum),
        false,
        true,
        this.isRubrikConfigured('detailGrundstueckEigentum'),
        this.getRubrikConfiguredIndex('detailGrundstueckEigentum'),
        this.router
      )
    );
    this.menuItems.push(
      new SubMenuItem(
        idObservable,
        of('/grundstueck/detail/dienstbarkeiten'),
        'grundstueck.subnav.Dienstbarkeiten',
        1,
        of(true),
        of(this.grundstueckEntry.permissions?.dienstbarkeiten),
        false,
        true,
        this.isRubrikConfigured('detailGrundstueckDienstbarkeiten'),
        this.getRubrikConfiguredIndex('detailGrundstueckDienstbarkeiten'),
        this.router
      )
    );
    this.menuItems.push(
      new SubMenuItem(
        idObservable,
        of('/grundstueck/detail/rangregelungen'),
        'grundstueck.subnav.Rangregelungen',
        1,
        of(true),
        of(this.grundstueckEntry.permissions?.rangregelungen),
        false,
        true,
        this.isRubrikConfigured('detailGrundstueckRangregelungen'),
        this.getRubrikConfiguredIndex('detailGrundstueckRangregelungen'),
        this.router
      )
    );
    this.menuItems.push(
      new SubMenuItem(
        idObservable,
        of('/grundstueck/detail/grundlasten'),
        'grundstueck.subnav.Grundlasten',
        1,
        of(true),
        of(this.grundstueckEntry.permissions?.grundlasten),
        false,
        true,
        this.isRubrikConfigured('detailGrundstueckGrundlasten'),
        this.getRubrikConfiguredIndex('detailGrundstueckGrundlasten'),
        this.router
      )
    );
    this.menuItems.push(
      new SubMenuItem(
        idObservable,
        of('/grundstueck/detail/vormerkungen'),
        'grundstueck.subnav.Vormerkungen',
        1,
        of(true),
        of(this.grundstueckEntry.permissions?.vormerkungen),
        false,
        true,
        this.isRubrikConfigured('detailGrundstueckVormerkungen'),
        this.getRubrikConfiguredIndex('detailGrundstueckVormerkungen'),
        this.router
      )
    );
    this.menuItems.push(
      new SubMenuItem(
        idObservable,
        of('/grundstueck/detail/anmerkungen'),
        'grundstueck.subnav.Anmerkungen',
        1,
        of(true),
        of(this.grundstueckEntry.permissions?.anmerkungen),
        false,
        true,
        this.isRubrikConfigured('detailGrundstueckAnmerkungen'),
        this.getRubrikConfiguredIndex('detailGrundstueckAnmerkungen'),
        this.router
      )
    );
    this.menuItems.push(
      new SubMenuItem(
        idObservable,
        of('/grundstueck/detail/pfandrechte'),
        'grundstueck.subnav.Grundpfandrechte',
        1,
        of(true),
        of(this.grundstueckEntry.permissions?.grundpfandrecht),
        false,
        true,
        this.isRubrikConfigured('detailGrundstueckGrundpfandrechte'),
        this.getRubrikConfiguredIndex('detailGrundstueckGrundpfandrechte'),
        this.router
      )
    );
    this.menuItems.push(
      new SubMenuItem(
        idObservable,
        of('/grundstueck/detail/haengige'),
        'grundstueck.subnav.HaengigeGeschaefte',
        1,
        of(true),
        of(this.grundstueckEntry.permissions?.haengigeGeschaefte),
        false,
        true,
        this.isRubrikConfigured('detailGrundstueckGeschaefteAuftraege'),
        this.getRubrikConfiguredIndex('detailGrundstueckGeschaefteAuftraege'),
        this.router
      )
    );
    this.menuItems.push(
      new SubMenuItem(
        idObservable,
        of('/grundstueck/detail/oereb'),
        'grundstueck.subnav.OerebKataster',
        0,
        this.checkErweiterung(ActiveErweiterungenEnum.OEREB_CH),
        of(true),
        false,
        false,
        of(true),
        of(20),
        this.router
      )
    );
    this.menuItems.push(
      new SubMenuItem(
        idObservable,
        of('/grundstueck/detail/neskoab'),
        'grundstueck.subnav.neskoab',
        0,
        this.checkErweiterung(ActiveErweiterungenEnum.NESKO_SCHNITTELLE),
        of(this.grundstueckEntry.permissions?.neskoAb),
        false,
        false,
        of(true),
        of(30),
        this.router
      )
    );
    this.menuItems.push(
      new SubMenuItem(
        idObservable,
        of('/grundstueck/detail/stime'),
        'grundstueck.subnav.neskoab',
        0,
        this.checkErweiterung(ActiveErweiterungenEnum.STIME_SCHNITTSTELLE),
        of(this.grundstueckEntry.permissions?.stimeAmtlicheBewertung),
        false,
        false,
        of(true),
        of(30),
        this.router
      )
    );
    this.menuItems.push(
      new GwrSubmenuItem(
        idObservable,
        of('/grundstueck/detail/gwrobjekte'),
        'grundstueck.subnav.GwrObjekte',
        0,
        this.checkErweiterung(ActiveErweiterungenEnum.GWR_INTEGRATION),
        of(this.grundstueckEntry.permissions?.gwrObjekte),
        false,
        false,
        this.router,
        this.grundstueckEntry.egrid,
        this.grundstueckEntry.grundstueckNummern[0].gbGemeinde.bfsNummer,
        this.grundstueckEntry.grundstueckNummern[0].nummer
      )
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private isRubrikConfigured(rubrikComponent): Observable<boolean> {
    return this.grundstueckRubriken$.pipe(map((a) => a.filter((e) => e.element === rubrikComponent).length > 0));
  }

  private getRubrikConfiguredIndex(rubrikComponent): Observable<number> {
    return this.grundstueckRubriken$.pipe(
      map((a) => {
        const found = a.filter((e) => e.element === rubrikComponent);
        if (found) {
          return a.indexOf(found[0]);
        }
        return -1;
      })
    );
  }

  private init(id) {
    this.grundstueckId = id;
    this.menuItems = [];
    this.ngOnInit();
  }

  private checkErweiterung(activeErweiterung: ActiveErweiterungenEnum): Observable<boolean> {
    return this.appFormatConfigService.erweiterungsConfig$.pipe(map((config) => config?.activeErweiterungen.includes(activeErweiterung)));
  }
}

@Directive()
class SubMenuItem implements OnDestroy {
  id: string;
  public link: string;
  public translateKey: string;
  public hasPermission: boolean;
  isSelected: boolean;
  cssClasses;
  private readonly menuLevel: number;
  private isErweiterung: boolean;
  private readonly isExternal: boolean;
  private readonly showDeactivated: boolean;
  private isRubrikConfigured: boolean;
  private menuLevelOrder: number;
  private router: Router;
  private subscription: Subscription = new Subscription();

  constructor(
    id$: Observable<string>,
    link$: Observable<string>,
    translateKey: string,
    menuLevel: number,
    isErweiterung$: Observable<boolean>,
    hasBenutzerrecht$: Observable<boolean>,
    isExternal: boolean,
    showDeactivated: boolean,
    isRubrikConfigured$: Observable<boolean>,
    menuLevelOrder$: Observable<number>,
    router: Router
  ) {
    this.translateKey = translateKey;
    this.subscription.add(isErweiterung$.subscribe((r) => (this.isErweiterung = r)));
    this.subscription.add(hasBenutzerrecht$.subscribe((r) => (this.hasPermission = r)));
    this.subscription.add(id$.subscribe((id) => (this.id = id)));
    this.subscription.add(link$.subscribe((l) => (this.link = l)));
    this.subscription.add(isRubrikConfigured$.subscribe((r) => (this.isRubrikConfigured = r)));
    this.subscription.add(menuLevelOrder$.subscribe((o) => (this.menuLevelOrder = o)));
    this.isExternal = isExternal;
    this.showDeactivated = showDeactivated;
    this.router = router;
    this.menuLevel = menuLevel;
    this.cssClasses = this.getCssClasses();
    this.subscription.add(
      this.router.events
        .pipe(
          filter((e) => e instanceof NavigationEnd),
          startWith(null)
        )
        .subscribe(() => {
          this.isSelected = this.isLinkActive(this.link);
        })
    );
  }

  get menuOrder() {
    return this.menuLevel * 10 + this.menuLevelOrder;
  }

  get isInternalLink() {
    return !this.isExternal && this.isErweiterung && this.hasPermission && this.isRubrikConfigured;
  }

  get isExternalLink() {
    return this.isExternal && this.isErweiterung && this.hasPermission && this.isRubrikConfigured;
  }

  get deactivatedLink() {
    return this.isErweiterung && !this.hasPermission && this.showDeactivated && this.isRubrikConfigured;
  }

  getCssClasses() {
    const classes = [];
    if (!this.hasPermission) {
      classes.push('deactivated');
    }
    classes.push('menuindent' + this.menuLevel);
    return classes;
  }

  isLinkActive(url): boolean {
    const queryParamsIndex = this.router.url.indexOf('?');
    const baseUrl = queryParamsIndex === -1 ? this.router.url : this.router.url.slice(0, queryParamsIndex);
    return baseUrl === url;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}

@Directive()
class GwrSubmenuItem extends SubMenuItem {
  public egrid: Egrid;
  public bfsnummer: number;
  public grundstuecknummer: string;

  constructor(
    id$: Observable<any>,
    link$: Observable<string>,
    translateKey: string,
    menuLevel: number,
    isErweiterung$: Observable<boolean>,
    hasBenutzerrecht$: Observable<boolean>,
    isExternal: boolean,
    showDeactivated: boolean,
    router: Router,
    egrid: Egrid,
    bfsnummer: number,
    grundstuecknummer: string
  ) {
    super(id$, link$, translateKey, menuLevel, isErweiterung$, hasBenutzerrecht$, isExternal, showDeactivated, of(true), of(40), router);
    this.egrid = egrid;
    this.bfsnummer = bfsnummer;
    this.grundstuecknummer = grundstuecknummer;
  }
}
